import type { GeofenceSetup } from "../domain/geofenceReminder";
import type { GeoPoint } from "../domain/location";

/** A tag the NFC reader of the device read (T-174). */
export interface NfcReading {
  /** UID of the tag in upper-case hexadecimal notation, or `null` if the system does not tell it. */
  readonly uid: string | null;
  /** The first URI of the tag's NDEF message (the SUN URL of a workplace tag), or `null`. */
  readonly url: string | null;
}

/**
 * Outcome of a reading (T-174): a tag, nothing (cancelled, timed out or failed), or NFC switched off in the system
 * settings.
 */
export type NfcReadResult =
  | { readonly kind: "read"; readonly reading: NfcReading }
  | { readonly kind: "cancelled" }
  | { readonly kind: "disabled" };

/** Longest wait for a tag after the person started a reading (T-174). */
export const nfcReadTimeoutMs = 30_000;

/**
 * Longest wait for a position, the system's permission dialog included: the time limit of a position
 * request counts only after the permission, and clocking must never wait long for it (T-175).
 */
export const positionOverallLimitMs = 20_000;

/** State of a permission of the geofence reminder. */
export type GeofencePermission = "granted" | "denied" | "prompt";

/**
 * What the device can do for the geofence reminder and what the person allowed (T-188): `regions`
 * reminds on arriving at an own establishment (iOS, Android stage „geofence“), `shiftStart` only at
 * the start of a shift (the standard Android build, which may not use the location in the
 * background).
 */
export interface GeofenceStatus {
  /** What the device reminds of. */
  readonly mode: "regions" | "shiftStart";
  /** Local notifications. */
  readonly notifications: GeofencePermission;
  /** The location „while using the app“ (iOS) or the precise location (Android). */
  readonly location: GeofencePermission;
  /** The location in the background (Android 10 and later); like `location` elsewhere. */
  readonly background: GeofencePermission;
  /** Whether the location is precise; iOS may not remind with the approximate location. */
  readonly precise: boolean;
}

/**
 * The geofence reminder of the native apps (spec 12.4, T-188, ADR-0167): the system watches the
 * regions of the own establishments and shows a local notification; the app never reads a position
 * for it and sends nothing. A tapped reminder is delivered through
 * {@link PlatformServices.onNotificationOpened} with the path of the clock screen.
 */
export interface GeofenceReminders {
  /**
   * Whether a region reminds only once per arming (iOS shows it without waking the app, so the app
   * arms it again in the foreground while work is due).
   */
  readonly oneShotRegions: boolean;
  /** Whether leaving a region while clocked in is reminded too (Android only). */
  readonly remindsOnExit: boolean;
  /**
   * Reads what the device can do and what the person allowed.
   *
   * @returns The status.
   */
  status(): Promise<GeofenceStatus>;
  /**
   * Asks for the permissions after the person switched the reminder on, one system dialog after the
   * other; never for the location „always“ on iOS.
   *
   * @returns The status afterwards.
   */
  requestPermissions(): Promise<GeofenceStatus>;
  /**
   * Keeps the setup on the device and registers its regions or the alarm at the next shift start.
   *
   * @param setup - The setup.
   */
  configure(setup: GeofenceSetup): Promise<void>;
  /** Removes the setup, every region, the alarm and the reminders shown (revocation, sign-out). */
  clear(): Promise<void>;
  /** Opens the settings of the app, where the person changes permissions the system no longer asks for. */
  openSettings(): Promise<void>;
}

/** Runtime platforms of the application. */
export type PlatformName = "web" | "ios" | "android";

/**
 * Abstraction over platform-specific capabilities (spec 12.1). The implementation is chosen once
 * in the composition root: {@link WebPlatformServices} in the browser and the installed PWA,
 * {@link NativePlatformServices} in the Capacitor apps. Components and use cases never ask
 * `Capacitor.isNativePlatform()` themselves.
 */
export interface PlatformServices {
  /** The platform the application runs on. */
  readonly name: PlatformName;

  /** Whether the application runs inside a native Capacitor app. */
  readonly isNative: boolean;

  /**
   * Returns whether the device currently has network connectivity. `true` does not guarantee
   * that the server is reachable.
   *
   * @returns `true` if online.
   */
  isOnline(): Promise<boolean>;

  /**
   * Subscribes to connectivity changes.
   *
   * @param listener - Called with the new connectivity.
   * @returns A function that ends the subscription.
   */
  onNetworkChange(listener: (online: boolean) => void): () => void;

  /**
   * Subscribes to the application coming to the foreground (to trigger a synchronization).
   *
   * @param listener - Called on every resume.
   * @returns A function that ends the subscription.
   */
  onResume(listener: () => void): () => void;

  /**
   * Returns whether the application is currently in the foreground.
   *
   * @returns `true` if visible to the person.
   */
  isInForeground(): boolean;

  /**
   * Subscribes to URLs that open the application (private-use URI scheme of the native apps,
   * for example the login callback). The URL that launched the app is delivered once as well.
   * In the browser, no URL is ever delivered.
   *
   * @param listener - Called with the full URL.
   * @returns A function that ends the subscription.
   */
  onAppUrlOpen(listener: (url: string) => void): () => void;

  /**
   * Subscribes to taps on push notifications of the native apps. The listener receives the
   * internal application path of the notification. In the browser the service worker opens the
   * page itself, so nothing is delivered.
   *
   * @param listener - Called with the application path.
   * @returns A function that ends the subscription.
   */
  onNotificationOpened(listener: (path: string) => void): () => void;

  /**
   * Shows the number of open working days on the app icon (spec 12.2, T-082); `0` removes the
   * badge. Platforms without badge counts ignore it.
   *
   * @param count - Number of open working days.
   */
  setBadge(count: number): Promise<void>;

  /**
   * Removes the notifications delivered to the device, once the server reports no open working day
   * or the person signs out; on Android this also removes the launcher dot (F-14). Platforms that
   * do not manage delivered notifications ignore it.
   */
  clearDeliveredReminders(): Promise<void>;

  /**
   * Asks once for the coarse position of the device at the moment of clocking (spec 12.4, T-175):
   * only in the foreground, never in the background, with the permission „while using the app“,
   * and rounded to three decimal places on the device. A refused permission, a missing result or
   * an elapsed time limit give `null`; the caller clocks without a location then. The whole request,
   * permission dialog included, ends after {@link positionOverallLimitMs}. Absent on platforms
   * without the capability.
   *
   * @param timeoutMs - Time limit of the position request.
   * @returns The rounded position, or `null`.
   */
  currentPosition?(timeoutMs: number): Promise<GeoPoint | null>;

  /**
   * Returns whether the device has an NFC reader (spec 12.4, T-174); a device without one never shows NFC
   * functions. Absent on platforms without the capability (the browser: Web NFC is not used).
   *
   * @returns `true` if NFC hardware is present.
   */
  nfcSupported?(): Promise<boolean>;

  /**
   * Reads one tag after an explicit action of the person (`SINV-01`): iOS shows the system sheet with the prompt
   * (Core NFC, iPhones only), Android reads in the foreground while the call runs. Nothing is read in the background.
   * A cancelled or failed reading and an elapsed {@link nfcReadTimeoutMs} give `cancelled`, NFC switched off in the
   * settings gives `disabled`. Absent on platforms without the capability.
   *
   * @param prompt - Text of the system sheet (iOS).
   * @returns The outcome.
   */
  readNfcTag?(prompt: string): Promise<NfcReadResult>;

  /**
   * Ends a running {@link readNfcTag} as cancelled (the button „Abbrechen“ of the app; iOS has its own in the sheet).
   * Absent on platforms without the capability.
   */
  cancelNfcRead?(): void;

  /**
   * Watches for tags held to the device while the app is in the foreground, for the sign-in with a personal chip at
   * an Android terminal (variant B, T-174). Absent on iOS (variant A only) and in the browser.
   *
   * @param listener - Called with every reading.
   * @returns A function that ends the watch.
   */
  readonly watchNfcTags?: (listener: (reading: NfcReading) => void) => () => void;

  /**
   * The geofence reminder of the native apps (spec 12.4, T-188). Absent in the browser, where the
   * function is hidden.
   */
  readonly geofence?: GeofenceReminders;
}
